import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../lib/Seo";
import RevealText from "../lib/RevealText";
import Reveal from "../lib/Reveal";
import { itineraries } from "../data/itineraries";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { state } = useLocation();
  const preselect = state?.journey || "";
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    journey: preselect,
    travel_dates: "",
    group_size: "",
    message: "",
    special_requirements: "",
  });

  useEffect(() => {
    if (preselect) setForm((f) => ({ ...f, journey: preselect }));
  }, [preselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success("Consultation requested — a specialist will be in touch shortly.");
    } catch (err) {
      toast.error("Something interrupted the connection. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Njia Safaris — Begin Planning Your African Journey"
        description="Begin planning your private African safari with Njia Safaris. Every journey is composed by hand around your dates, pace and appetite for wildness."
        path="/contact"
      />
      <Nav />
      <Toaster position="top-center" richColors />

      {/* Hero */}
      <section className="pt-40 pb-16 md:pt-56 md:pb-16 container-x" data-testid="contact-hero">
        <p className="eyebrow mb-6">Enquire · Begin the conversation</p>
        <RevealText
          as="h1"
          className="h-display text-[12vw] md:text-[8vw]"
          lines={["Compose", <em key="e" className="italic text-[color:var(--forest)]">your journey.</em>]}
        />
        <p className="mt-8 max-w-2xl text-lg leading-relaxed">
          Every enquiry is answered personally within one working day. Share whatever you know — dates, appetite, dreams —
          and one of our planners will begin composing your path.
        </p>
      </section>

      {/* Form */}
      <section className="container-x pb-24" data-testid="contact-form-section">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-8">
            {sent ? (
              <Reveal>
                <div
                  className="relative border border-[color:var(--gold)]/40 bg-gradient-to-br from-[color:var(--ivory-2)] to-[color:var(--sand)] p-8 md:p-14 overflow-hidden"
                  data-testid="contact-thankyou"
                >
                  {/* Corner accent */}
                  <div
                    className="absolute top-0 left-0 h-16 w-16 border-t-2 border-l-2"
                    style={{ borderColor: "var(--gold)" }}
                    aria-hidden
                  />
                  <div
                    className="absolute bottom-0 right-0 h-16 w-16 border-b-2 border-r-2"
                    style={{ borderColor: "var(--gold)" }}
                    aria-hidden
                  />

                  <div className="flex items-center gap-3 mb-6">
                    <CheckCircle2
                      size={22}
                      strokeWidth={1.4}
                      className="text-[color:var(--gold-2)]"
                    />
                    <span className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-2)]">
                      Consultation requested
                    </span>
                  </div>

                  <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mb-2">
                    Thank you,{" "}
                    <em className="italic text-[color:var(--forest)]">
                      {form.name.split(" ")[0] || "friend"}.
                    </em>
                  </h2>
                  <p className="font-italic-display text-xl md:text-2xl text-[color:var(--forest)] mb-8 max-w-2xl">
                    Your request is now with our specialists.
                  </p>

                  <div className="hairline mb-8" />

                  <div className="grid md:grid-cols-3 gap-6 md:gap-10 mb-10">
                    <div>
                      <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--forest)] mb-2">
                        Step 01
                      </p>
                      <p className="text-[15px] leading-relaxed">
                        A senior Njia specialist will review your request personally within one working day.
                      </p>
                    </div>
                    <div>
                      <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--forest)] mb-2">
                        Step 02
                      </p>
                      <p className="text-[15px] leading-relaxed">
                        They will reach out to arrange a private consultation — email, phone, or video, at your convenience.
                      </p>
                    </div>
                    <div>
                      <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--forest)] mb-2">
                        Step 03
                      </p>
                      <p className="text-[15px] leading-relaxed">
                        A private proposal is composed for you, one hand, one journey — usually within seven days.
                      </p>
                    </div>
                  </div>

                  <div className="hairline mb-8" />

                  <p className="text-[14px] leading-relaxed text-[color:var(--ink)]/75 max-w-2xl mb-4">
                    In the meantime, feel free to browse our{" "}
                    <a href="/journeys" className="underline decoration-[color:var(--gold)] underline-offset-4 hover:text-[color:var(--forest)]">
                      signature journeys
                    </a>{" "}
                    or read about the{" "}
                    <a href="/signature-journey" className="underline decoration-[color:var(--gold)] underline-offset-4 hover:text-[color:var(--forest)]">
                      2026 Regenerative Safari
                    </a>
                    . The plains are already waiting.
                  </p>

                  <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--ink)]/50 mt-8">
                    A confirmation has been logged to our planning desk · Ref{" "}
                    <span className="text-[color:var(--forest)]">
                      {new Date().getFullYear()}-
                      {Math.random().toString(36).substring(2, 8).toUpperCase()}
                    </span>
                  </p>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={submit} className="border-t border-[color:var(--ink)] pt-10 space-y-8" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                  <div>
                    <label className="field-label" htmlFor="name">Your name</label>
                    <input
                      id="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={set("name")}
                      className="field-input"
                      placeholder="Amelia Hart"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      className="field-input"
                      placeholder="you@domain.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                  <div>
                    <label className="field-label" htmlFor="phone">Phone (optional)</label>
                    <input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className="field-input"
                      placeholder="+44 20 …"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="journey">Interested journey</label>
                    <select
                      id="journey"
                      value={form.journey}
                      onChange={set("journey")}
                      className="field-input"
                      data-testid="contact-journey"
                    >
                      <option value="">Open — advise me</option>
                      {itineraries.map((i) => (
                        <option key={i.slug} value={i.name}>
                          {i.name} · {i.country}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                  <div>
                    <label className="field-label" htmlFor="dates">Approximate dates</label>
                    <input
                      id="dates"
                      type="text"
                      value={form.travel_dates}
                      onChange={set("travel_dates")}
                      className="field-input"
                      placeholder="September 2026, flexible"
                      data-testid="contact-dates"
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="group">Group size</label>
                    <input
                      id="group"
                      type="text"
                      value={form.group_size}
                      onChange={set("group_size")}
                      className="field-input"
                      placeholder="Two adults"
                      data-testid="contact-group"
                    />
                  </div>
                </div>
                <div>
                  <label className="field-label" htmlFor="message">A note to your planner</label>
                  <textarea
                    id="message"
                    value={form.message}
                    onChange={set("message")}
                    className="field-input"
                    rows={5}
                    placeholder="What are you dreaming of?"
                    data-testid="contact-message"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="special_requirements">
                    Special requirements <span className="normal-case tracking-normal text-[color:var(--ink)]/50">(optional)</span>
                  </label>
                  <textarea
                    id="special_requirements"
                    value={form.special_requirements}
                    onChange={set("special_requirements")}
                    className="field-input"
                    rows={3}
                    placeholder="Dietary needs, mobility, celebrations, photography priorities, quiet arrivals — anything we should know."
                    data-testid="contact-special-requirements"
                  />
                </div>
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-gold disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {submitting ? "Sending…" : "Request a Consultation"}
                    <ArrowUpRight size={14} strokeWidth={1.6} />
                  </button>
                </div>
              </form>
            )}
          </div>

          <aside className="md:col-span-4">
            <div className="border-t border-[color:var(--ink)] pt-10">
              <p className="eyebrow mb-4">Direct correspondence</p>
              <p className="font-display text-2xl md:text-3xl break-all">praveen.nagpal@njiasafaris.com</p>
              <div className="hairline my-8" />
              <p className="eyebrow mb-4">Offices</p>
              <ul className="space-y-2 text-[15px]">
                <li>Noida · India <span className="text-[color:var(--ink)]/50">— Head office</span></li>
                <li>Nairobi & Arusha · East Africa <span className="text-[color:var(--ink)]/50">— On the ground</span></li>
              </ul>
              <div className="hairline my-8" />
              <p className="eyebrow mb-4">Response</p>
              <p className="text-[15px] leading-relaxed">
                Within one working day, personally, from the planner who will compose your journey.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </>
  );
}
